/* eslint-disable max-lines */
import {
  DestroyRef,
  ElementRef,
  ChangeDetectionStrategy,
  Component,
  computed,
  Injector,
  input,
  LOCALE_ID,
  output,
  runInInjectionContext,
  viewChild,
  Signal,
  inject as inject_1,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';

import type { ECharts, ECElementEvent } from 'echarts/core';
import type { CallbackDataParams, EChartsOption, TooltipOption } from 'echarts/types/dist/shared';
import {
  animationFrameScheduler,
  BehaviorSubject,
  buffer,
  combineLatest,
  delay,
  fromEvent,
  merge,
  of,
  pairwise,
  ReplaySubject,
  switchMap,
} from 'rxjs';
import {
  debounceTime,
  filter,
  map,
  observeOn,
  share,
  shareReplay,
  skip,
  take,
} from 'rxjs/operators';

import { createProviderToken } from '@core/common-legacy';
import {
  ReportsChartOptions,
  ReportDashboardComponentChartType,
  VisualizationData,
  ReportDashboardComponentViewData,
  ReportDashboardComponentKind,
} from '@core/reports-types';
import type { Theme } from '@core/theme-util';
import { ResizeObserverEntryDirective, WindowRef } from '@core/user-interface-legacy';
import { injectUntilDestroyed } from '@core/utils-legacy';

import { ChartMomentConfigService } from './chart-moment-config.service';
import {
  addTooltip,
  chartFactory,
  dataZoomRange,
  exportOptions,
  getChartOptions,
  getChartThemeStylingOptions,
} from '../../visualizations';
import { getDefaultTooltipPosition } from '../../visualizations/strategies/composables/tooltip';
import { ReportTooltipComponent } from '../tooltip/report-tooltip.component';

const { provide: provideReportsTooltipContainer, inject: injectContainerEl } = createProviderToken<
  Signal<HTMLElement | null>
>('reportsTooltipContainerEl')();

export { provideReportsTooltipContainer };

@Component({
  selector: 'reports-legacy-chart',
  template: `
    <div
      [rtResizeObserver]="{ initial: true }"
      (resized)="size$.next({ width: $event.width, height: $event.height })"
      #chartOutlet
      id="chart-outlet"
      layout
    ></div>
  `,
  styleUrls: ['./legacy-chart.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ResizeObserverEntryDirective],
})
export class ReportsLegacyChartComponent {
  public static deviceDpiChange$ = fromEvent(matchMedia('(resolution: 1dppx)'), 'change').pipe(
    debounceTime(100),
    observeOn(animationFrameScheduler),
    share(),
  );

  private readonly windowRef = inject_1(WindowRef);
  private readonly momentConfigService = inject_1(ChartMomentConfigService);
  private readonly locale = inject_1(LOCALE_ID);
  private readonly injector = inject_1(Injector);

  private readonly containerEl = injectContainerEl({ optional: true });

  public chart?: ECharts;

  public component = input.required<ReportDashboardComponentViewData>();
  public data = input.required<VisualizationData<ReportsChartOptions>>();
  public resourcesPreviewAvailable = input.required<boolean>();
  public theme = input.required<Theme>();
  public export = input<boolean>(false);
  public openResourceAt = output<[number, number]>();
  public rendered = output<void>();
  public size$ = new ReplaySubject<{ width: number; height: number }>(1);

  public chartOutlet = viewChild.required<ElementRef<HTMLDivElement>>('chartOutlet');

  private untilDestroyed = injectUntilDestroyed();
  private dataRecordsSize = computed(() => this.data().dataset.datasetSize.rows - 1);
  private scheduledPaint?: number;

  private chartType = computed(() => this.component().chartType!);
  private isDatasetReady$ = toObservable(this.data).pipe(
    map(data => !data.dataset.isInitial),
    shareReplay(1),
  );

  private activeElement$ = new BehaviorSubject<CallbackDataParams | null>(null);
  // debounce to preven flicker when mouse moves between series
  private activeElementDebounced: CallbackDataParams | null = null;

  constructor() {
    const destroyRef = inject_1(DestroyRef);

    this.momentConfigService.init();
    this.activeElement$
      .pipe(
        switchMap(el => {
          if (el === null) {
            return of(null).pipe(delay(1));
          }
          return of(el);
        }),
        this.untilDestroyed,
      )
      .subscribe(el => (this.activeElementDebounced = el));
    destroyRef.onDestroy(() => this.disposeChart());
  }

  public ngOnInit() {
    const deviceDpiChanged$ = ReportsLegacyChartComponent.deviceDpiChange$.pipe();

    merge(this.size$.pipe(skip(1)), deviceDpiChanged$)
      .pipe(this.untilDestroyed)
      .subscribe(() => this.repaintChart());
  }

  public ngAfterViewInit() {
    const containerReady$ = this.size$.pipe(map(({ width, height }) => width > 0 && height > 0));

    combineLatest([this.isDatasetReady$, containerReady$])
      .pipe(
        filter(([x, y]) => x && y),
        take(1),
      )
      .subscribe(() => {
        this.createChart();
        this.updateChart();

        runInInjectionContext(this.injector, () => {
          this.listenToChanges();
        });
      });
  }

  public ngOnDestroy() {
    this.forceHideTooltip();
  }

  public forceHideTooltip() {
    if (this.chart) this.chart.dispatchAction({ type: 'hideTip' });
  }

  private listenToChanges() {
    const chartTypeChanged$ = toObservable(this.component).pipe(
      pairwise(),
      filter(([prev, curr]) => prev.chartType !== curr.chartType),
      map(() => <const>'restart'),
    );

    const themeChanged$ = toObservable(this.theme).pipe(
      skip(1),
      map(() => <const>'restart'),
    );

    const dataChanged$ = toObservable(this.data).pipe(
      filter(data => !data.dataset.isInitial),
      pairwise(),
      filter(([prev, curr]) => prev.dataset.checksum !== curr.dataset.checksum),
      map(() => <const>'update'),
    );

    const source$ = merge(chartTypeChanged$, themeChanged$, dataChanged$).pipe(shareReplay());

    source$
      .pipe(
        // get together all values emitted in 1ms window,
        // most likely when all inputs changed at once
        buffer(source$.pipe(debounceTime(1, animationFrameScheduler))),
        map(actions =>
          actions.reduce((acc, action) => acc.add(action), new Set<'update' | 'restart'>()),
        ),
        filter(actions => actions.size > 0),
        this.untilDestroyed,
      )
      .subscribe(action => {
        if (action.has('restart')) {
          this.disposeChart();
          this.createChart();
        }
        this.schedulePaint(() => this.updateChart());
      });
  }

  private repaintChart() {
    if (!this.chart) {
      return;
    }
    // @ts-ignore
    const dataZoomComponent = this.chart.getModel().getComponent('dataZoom');

    if (dataZoomComponent) {
      this.adjustDataZoom(dataZoomComponent, this.chart);
    }

    this.chart.resize({
      silent: true,
    });
  }

  private createChart() {
    this.chart = chartFactory(
      this.chartOutlet().nativeElement,
      this.theme(),
      this.windowRef.nativeWindow.devicePixelRatio,
      this.locale,
      this.export(),
    );

    if (this.resourcesPreviewAvailable()) {
      this.chart.on('click', 'series', this.openResourceHandler);
    }
  }

  private updateChart() {
    const chart = this.chart;
    if (!chart) {
      return;
    }
    chart.clear();

    // TODO: find better way of reading component from chart instance, as getModel() is private (thus ts-ignore)
    // @ts-ignore
    const model = chart.getModel();
    let dataZoomComponent = model?.getComponent('dataZoom');

    const optionsUpdates = this.getChartOptionsUpdates(
      this.chartType(),
      this.data().vis,
      chart,
      this.theme(),
    );
    optionsUpdates.forEach((update, index, arr) => {
      if (index === arr.length - 1) {
        // callback has to be registered just before last option are set
        dataZoomComponent = model?.getComponent('dataZoom');
        if (this.export() || !dataZoomComponent) {
          chart.on('finished', this.onFinished);
        }
      }
      const options = update();
      chart.setOption(options, false, false);
    });

    if (dataZoomComponent && !this.export()) {
      chart.on('finished', this.onFinished);
      this.adjustDataZoom(dataZoomComponent, chart);
    }
    chart.on('mouseover', this.onMouseOver);
    chart.on('mouseout', this.onMouseOut);
  }

  private getChartOptionsUpdates(
    type: ReportDashboardComponentChartType,
    options: ReportsChartOptions,
    chart: ECharts,
    theme: Theme,
  ): Array<() => EChartsOption> {
    const seriesCursor = this.resourcesPreviewAvailable() ? 'pointer' : 'default';
    const initialOpts = getChartOptions(type, this.decorateChartOptions(options));

    const chartOptions = () =>
      <EChartsOption>{
        ...initialOpts,
        series: initialOpts.series.map(s => ({
          ...s,
          cursor: seriesCursor,
        })),
        tooltip: this.buildTooltip(initialOpts),
      };
    const styleOptions = () => getChartThemeStylingOptions(<any>initialOpts, theme);
    const exportOpts = () =>
      exportOptions(
        <EChartsOption>chart.getOption(),
        this.data().dataset,
        this.chartOutlet().nativeElement.clientWidth,
        true,
      );

    const commonOptions = [chartOptions, styleOptions];
    if (this.export()) {
      commonOptions.push(exportOpts);
    }
    return commonOptions;
  }

  // very specific case that otherwise has nothing to do with how we style our charts
  private decorateChartOptions<T extends ReportsChartOptions>(options: T): T {
    let opts = { ...options };
    // if there is only 1 record, we want to force the options to show legend
    if (
      this.component().kind === ReportDashboardComponentKind.Trend &&
      this.component().primaryGroup
    ) {
      opts = {
        ...options,
        legend: {
          ...options.legend,
          show: true,
        },
      };
    }
    return opts;
  }

  private buildTooltip(opts: ReportsChartOptions): TooltipOption {
    const tooltip = addTooltip({
      containerEl: this.containerEl && this.containerEl(),
      injector: this.injector,
      componentClass: ReportTooltipComponent,
      memoizeFn: params =>
        `${this.data().dataset.checksum}${JSON.stringify(params)}${this.activeElementDebounced?.seriesId}`,
      componentInputs: params => {
        // legacy way of using the formatter.
        // previous implementation would override default tooltip behavior of echarts
        // so the type here is incorrect
        const p = Array.isArray(params) ? params : [params];
        const context = opts.tooltip!.formatter!(<any>p);
        return {
          context,
          activeId: this.activeElementDebounced?.seriesId,
        };
      },
    })(opts).tooltip;

    return {
      ...tooltip,
      position: getDefaultTooltipPosition(this.chartOutlet().nativeElement),
    };
  }

  private disposeChart() {
    this.forceHideTooltip();
    if (!this.chart) {
      return;
    }
    this.chart.off('click', this.openResourceHandler);
    this.chart.off('finished', this.onFinished);
    this.chart.off('mouseover', this.onMouseOver);
    this.chart.off('mouseout', this.onMouseOut);
    this.chart.dispose();
    this.chart = undefined;
  }

  private openResourceHandler = (params: CallbackDataParams) => {
    this.openResourceAt.emit([params.dataIndex, params.seriesIndex!]);
  };

  private onFinished = () => {
    this.rendered.emit();
  };

  private onMouseOver = (ev: ECElementEvent) => {
    this.activeElement$.next(ev);
  };
  private onMouseOut = (ev: ECElementEvent) => {
    this.activeElement$.next(null);
  };

  private getDataZoomLimits(height: number) {
    const isGrouppedRate =
      this.component().kind === ReportDashboardComponentKind.Funnel &&
      this.data().dataset.dimensions.length > 1;
    const itemSize = isGrouppedRate ? 60 : 30;
    const limit = Math.min(Math.floor(height / itemSize), this.dataRecordsSize());
    return dataZoomRange(limit);
  }

  private adjustDataZoom(dataZoomComponent: any, chart: ECharts) {
    const { clientHeight: height } = this.chartOutlet().nativeElement;
    const nextDataZoomLimits = this.getDataZoomLimits(height);
    const show = nextDataZoomLimits.endValue !== this.dataRecordsSize();

    dataZoomComponent.getModel().mergeOption({ show });
    chart.dispatchAction({
      type: 'dataZoom',
      ...nextDataZoomLimits,
    });
  }

  private schedulePaint(callback: FrameRequestCallback) {
    if (this.scheduledPaint) {
      cancelAnimationFrame(this.scheduledPaint);
    }

    this.scheduledPaint = requestAnimationFrame(callback);
  }
}
